import React, {
  useState,
  useEffect,
  Suspense,
  useRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useMemo,
  startTransition,
} from "react";
import { prefetchDNS, preconnect } from "react-dom";
import Html from './html';
import { LazyLoadModules } from "./helpers/lazyLoadModules";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import "./styles/siteStyles.scss"
import './index.scss';
import './cookie.scss'
import { ANALYTICSEVENTS } from "./constants/analyticsEvents";
import logger from "./logger";
import loadable from '@loadable/component'
import type { SnackType } from "./components/Snackbar";
import NavFocusSkipper from "./components/SkipToMainContent";
import { ErrorBoundary } from "react-error-boundary";
import * as helpers from "./helpers";
import { isUserSubscribed } from "./helpers/checkSubscription";
import pubsub from "@viewlift/pubsub";
import { loadSiteStyles } from "./styles/helpers";
import { blockPreviewNavigation, isNavigationBlockedByPreview } from "./helpers/previewMode";
import { initializeConnection, syncSSEStore } from "./SSE";
import { parseJSON } from "./OnBoarding-V2/utils/parseJSON";
import { isKeycloakEnabled, handleKeycloakCallback, initKeycloakSignIn } from "./OnBoarding-V2/LoginV2/Helpers/keycloakLogin";
import { enableWebTelemetry, trackWebRoute, syncWebTelemetry } from "./helpers/webTelemetry";

// let Snackbar: ForwardRefExoticComponent<RefAttributes<SnackType>> | null
declare const window: any;
interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  sources: Array<any>;
}
const afterPaint = (fn: () => void) => {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(fn, { timeout: 4000 });
  } else {
    setTimeout(fn, 3000);
  }
};

const NotFound404 = React.lazy(() => import("./components/404"));
import CmsMenu from "./components/CmsMenu/index.js";
import PersonalizationBanner from './modules/PersonalizationBanner';
import { checkNbaAuth } from "./components/NBA/Auth/helper.js";
import { getStorageItem } from "./helpers/storageManager.js";
const V2TveLogin = loadable(() => import("./components/AdobeRestV2/V2TveLogin"));
const AuthZFailPopupContainer = loadable(() => import("./components/Toolbox/AuthZFailPopupContainer"));
const SplashLoader = loadable(() => import("./modules/SplashScreens"));
const UpgradeWrapper = loadable(() => import("./OnBoarding-V2/utils/UpgradeWrapper/index"));
const LocationServicesModal = loadable(() => import('./components/LocationServicesModal'));
const ContinueSubscriptionModal = loadable(() => import('./components/NBA/ContinueSubscriptionModal'));
const VerticalMastheadComponent = loadable(() => import('./modules/VerticalMasthead'), { ssr: true });
const MastheadComponent = loadable(() => import('./modules/MastheadModule'), { ssr: true });

export const App = ({siteConfig, pageData, browserDetails,xApiKey, cachedAPI,apiBaseUrl,sseBaseUrl,analyticsBaseUrl,wssBaseUrl,env,ip, embedCodeToken,toolsUrl,telemetryIngestEndpoint,templateUrl,requestId, cssHref, scriptSrcs, isPreview}: {siteConfig: any, pageData: any, browserDetails: any, xApiKey: any, cachedAPI: any, apiBaseUrl: any, sseBaseUrl: any, analyticsBaseUrl: any, wssBaseUrl: any, env: any, ip: any, embedCodeToken: any, toolsUrl: any, telemetryIngestEndpoint?: string, templateUrl: any, requestId: any, cssHref?: string | null, scriptSrcs?: string[] | null, isPreview?: boolean}) =>{
  // React 19 asset-hint APIs: emitted into the streamed <head> during SSR and
  // deduped automatically. Establishes the API/SSE/analytics connections in
  // parallel with the document download instead of waiting for hydration.
  if (apiBaseUrl) preconnect(apiBaseUrl, { crossOrigin: 'anonymous' });
  if (sseBaseUrl) prefetchDNS(sseBaseUrl);
  if (analyticsBaseUrl) prefetchDNS(analyticsBaseUrl);

  const [ pageMetaData, setPageData ] = useState(pageData)
  // In preview mode the page comes from the template builder payload, not the CMS.
  // Re-fetching would replace the unpublished page with the published one.
  const isPreviewMode = isPreview ?? (typeof window !== "undefined" ? Boolean(window.isPreview) : false);
  const pageMetaDataRef = useRef(pageData)
  const [VLCookieConsent, setVLCookieConsent] = useState<any | null>(null);
  const [activeSplash, setActiveSplash] = useState<string | null>(null);
  const [isTveEnabled, setIsTveEnabled] = useState<boolean>(false);
  const [showNbaModal, setshowNbaModal] = useState<boolean>(false);
  const [SnackbarComponent, setSnackbarComponent] = useState<null | ForwardRefExoticComponent<RefAttributes<SnackType>>>(null);

  //const [showMasthead, setShowMasthead] = useState(false)
  //const windowSizeRef:any = useRef({ width: null });
  const [searchParams] = useSearchParams();
  const [isUpgrade, setIsUpgrade] = useState(false)
  const navigate = useNavigate()
  const snackbarRef = useRef<SnackType>(null);
  let previouslyFocusedElement = useRef<any>(null);
  const analyticListenerReady = useRef<Promise<void> | null>(null);
  let IntervalInstance:any;

  const keycloakEnabled = isKeycloakEnabled();
  const [keycloakCallbackHandled, setKeycloakCallbackHandled] = useState(false);

  const disableVlCookieConsent = (typeof window !== "undefined") ? (window?.app_data?.appcmsMain?.privacy?.disableVlCookieConsent || false) : false;
  const zipcodeEnabledForPersonalizationChoices = (typeof window !== "undefined") ? (window?.app_data?.appcmsMain?.geoRestrict?.zipcodeEnabledForPersonalizationChoices || false) : false;
  const isAuthWithPwdFlow = (typeof window !== "undefined") ? (window?.app_data?.appcmsMain?.ssoConfig?.nhl?.isEnabled || false) : false;
  const configMessages = (typeof window !== "undefined") ? {
    cookieModalHeadingTitle: window?.app_data?.appcmsMain?.genericMessages?.cookieModalHeadingTitle,
    cookieModalDescription: window?.app_data?.appcmsMain?.genericMessages?.cookieModalDescription,
    requiredCookieAccordionTitle: window?.app_data?.appcmsMain?.genericMessages?.requiredCookieAccordionTitle,
    requiredCookieAccordionDescription: window?.app_data?.appcmsMain?.genericMessages?.requiredCookieAccordionDescription,
    optionalCookieAccordionTitle: window?.app_data?.appcmsMain?.genericMessages?.optionalCookieAccordionTitle,
    optionalCookieAccordionDescription: window?.app_data?.appcmsMain?.genericMessages?.optionalCookieAccordionDescription,
    functionalCookieTitle: window?.app_data?.appcmsMain?.genericMessages?.functionalCookieTitle,
    functionalCookieDescription: window?.app_data?.appcmsMain?.genericMessages?.functionalCookieDescription,
    performanceCookieTitle: window?.app_data?.appcmsMain?.genericMessages?.performanceCookieTitle,
    performanceCookieDescription: window?.app_data?.appcmsMain?.genericMessages?.performanceCookieDescription,
    cookieAcceptBtnLabel: window?.app_data?.appcmsMain?.genericMessages?.cookieAcceptBtnLabel,
    cookieRejectBtnLabel: window?.app_data?.appcmsMain?.genericMessages?.cookieRejectBtnLabel,
    cookieSaveSettingsBtnLabel: window?.app_data?.appcmsMain?.genericMessages?.cookieSaveSettingsBtnLabel,
    cookieSettingsModalTitle: window?.app_data?.appcmsMain?.genericMessages?.cookieSettingsModalTitle,
    cookieSettingsModalDescription: window?.app_data?.appcmsMain?.genericMessages?.cookieSettingsModalDescription,
    cookieSettingsMoreInfoTitle: window?.app_data?.appcmsMain?.genericMessages?.cookieSettingsMoreInfoTitle,
    cookieSettingsMoreInfoDescription: window?.app_data?.appcmsMain?.genericMessages?.cookieSettingsMoreInfoDescription,
    cookieLetMeChooseButtonLabel: window?.app_data?.appcmsMain?.genericMessages?.cookieLetMeChooseButtonLabel,
    privacyPolicyLinkLabel: window?.app_data?.appcmsMain?.genericMessages?.privacyPolicyLinkLabel,
    contactUsLinkLabel: window?.app_data?.appcmsMain?.genericMessages?.contactUsLinkLabel
  } : {};

  const shouldShowLocationModal = zipcodeEnabledForPersonalizationChoices && isAuthWithPwdFlow;

  const showNbaContinueSbscriptioModal = () => {
    pubsub.subscribe("show-nbaSubscription-popup", (data:any) => {
      if(data){
        const isNbaEnabled = Boolean(siteConfig?.appcmsMain?.ssoConfig?.nba?.isEnabled);
        let store: any = getStorageItem('AuthenticationStore');
        let nbaSelectedPlan: any = getStorageItem('nbaSelectedPlan');
        let user = store?.user
        if(isNbaEnabled && user && user?.provider == "nba" && nbaSelectedPlan)
        setshowNbaModal(true)
      }
    })
  }
  
  let location = useLocation();
  const prevUrlRef = useRef(null);

  useEffect(() => {
    pageMetaDataRef.current = pageMetaData;
  }, [pageMetaData]);

  useEffect(() => {
    window.location.ref = prevUrlRef?.current;
    prevUrlRef.current = window?.location?.href;
    syncWebTelemetry();
    trackWebRoute(location.pathname);
  }, [location]);

  useEffect(() => {
    // Complete Keycloak OIDC redirect callback when present (login only).
    if (!keycloakEnabled || keycloakCallbackHandled) return;

    handleKeycloakCallback()
      .then((handled) => {
        setKeycloakCallbackHandled(handled);
      })
      .catch((err) => console.error(err));
  }, [keycloakEnabled, keycloakCallbackHandled]);

  useEffect(() => {
    afterPaint(() => import("./instrumentation").then(({ Instrumentation }) => Instrumentation.initSentry()));
    if (typeof window === "undefined") return;
    const isNbaEnabled = window?.app_data?.appcmsMain?.ssoConfig?.nba?.isEnabled || false
    showNbaContinueSbscriptioModal()
    isNbaEnabled && checkNbaAuth()
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isAdobeEnabled = window?.app_data?.appcmsPlatform?.analytics?.adobeConfig?.enabled;
    if (!isAdobeEnabled) return;
    afterPaint(() => {
    const appMeasurementUrl = window?.app_data?.appcmsPlatform?.analytics?.adobeConfig?.appMeasurement;
    const mediaSdkUrl = window?.app_data?.appcmsPlatform?.analytics?.adobeConfig?.mediaSdk;
    const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => resolve();
      script.onerror = (e) => reject(e);
    });
    loadScript(appMeasurementUrl)
      .then(() => loadScript(mediaSdkUrl))
      .then(() => {
        window.adobeMediaScriptLoaded = true;
        const Media = window?.ADB?.Media;
        const MediaConfig = window?.ADB?.MediaConfig;
        if (Media && MediaConfig) {
          import("./AdobeAnalytics/adobeAnalytics").then(({ setupMediaHeartbeat }) => {
            setupMediaHeartbeat(Media, MediaConfig);
          });
        }
      })
      .catch((err) => console.error("Adobe Analytics load error:", err));
    });
  }, []);

  useEffect(() => {
    if (disableVlCookieConsent) {
      afterPaint(() => {
      let oneTrustInterval: NodeJS.Timeout | null = null;
      oneTrustInterval = setInterval(function () {
        if (window?.OneTrust?.IsAlertBoxClosed()) {
          if (oneTrustInterval) clearInterval(oneTrustInterval)
          pubsub.publish("one-trust-setup", true)
        }
      }, 3000);
      });
    }
  }, []);

  useEffect(() => {
    afterPaint(async () => {
      const { default: Cookies } = await import("js-cookie");
      const importVLCookieConsent = async () => {
        try {
          const module = await import('@viewlift/cookie-consent/vlCookieConsent/esm/App');
          if ('VLCookieConsent' in module) {
            const VLCookieConsent = module.VLCookieConsent;
            startTransition(() => setVLCookieConsent(() => VLCookieConsent));
          } else if ('default' in module) {
            const moduleWithDefault = module as { default: React.ComponentType<any> };
            if (moduleWithDefault && moduleWithDefault.default) {
              const VLCookieConsent = moduleWithDefault.default;
              startTransition(() => setVLCookieConsent(() => VLCookieConsent));
            }
          } else {
            logger(`Invalid VLCookieConsent module structure: ${module}`, "error");
          }
        } catch (error) {
          logger(`Error importing VLCookieConsent: ${error}`, "error")
        }
        if (Cookies.get("cc_cookie")) {
          const getCookie = JSON.parse(Cookies.get("cc_cookie"))
          if (getCookie?.level?.includes("performanceCookie") && !window.fireGaEvents) {
            window.fireGaEvents = true
            helpers.initGTM();
          }
          if (getCookie?.level?.includes("performanceCookie")) {
            enableWebTelemetry();
          }
        }
      };
      const externalAuthUrl = helpers.isExternalAuthUrl() || null
      let isAppAccessing = (!externalAuthUrl && helpers.getQueryParams(window.location.href)) || null
      const isKetchEnabled = (window?.app_data?.appcmsPlatform?.privacy?.ketchCookie?.enable) || false
      
      if(typeof (window) !== undefined && !(window.location.href.indexOf("embed/player") > -1) && !isAppAccessing) {
        analyticListenerReady.current = import("./analyticListener").then(({ listenAnalyticEvents, listenFBEvents,listenConviva }) => {
          listenAnalyticEvents();
          listenFBEvents();
          listenConviva();
        });
        
        if(!externalAuthUrl){
          if (!disableVlCookieConsent) {
            importVLCookieConsent();
          } else if(isKetchEnabled) {
            const { default: ketchCookieConsent } = await import('./components/CookieConsent/ketch.js');
            ketchCookieConsent()
            window.fireGaEvents = true
            helpers.initGTM();
            enableWebTelemetry();
          } else {
            pubsub.subscribe("one-trust-setup",(data) => {
              let OptanonConsent = Cookies.get("OptanonConsent")
              const params = new URLSearchParams(OptanonConsent);
              const parsedData = Object.fromEntries(params.entries());
              let groups = helpers.convertToJSON(parsedData?.groups)
              if (groups) {
                if (groups['C0004'] && !window.fireGaEvents) {
                  window.fireGaEvents = true
                  helpers.initGTM()
                }
                if (groups['C0004']) {
                  enableWebTelemetry();
                }
              }
            })
          }
        }
      }
      helpers.subscriptionData()
    });
  }, []);

  useEffect(() => {
    if(typeof (window) !== undefined && window?.app_data?.appcmsMain.features.embeddedCode){
      afterPaint(() => {
        const isAppAccessing = helpers.getQueryParams(window.location.href) || null
        if(isAppAccessing){
          document.body.classList.add("remove-oneTrust");
        }
        helpers.embedCode()
      });
    }
  },[])

  useEffect(() => {
    if(!shouldShowLocationModal) return;
    afterPaint(async () => {
      try {
        const { checkTeamsAvailability } = await import("./helpers/checkTeamsAvailability");
        const isTeamsAvailable = await checkTeamsAvailability()
        if (isTeamsAvailable) {
          pubsub.publish('hide-location-services-modal');
        } else {
          pubsub.publish('show-location-services-modal');
        }
      } catch (error) {
        console.error('Error checking teams availability:', error);
      }
    });
  }, []);


  const checkForIsRedirected = async () => {
    const [{ getStorageItem, setStorageItem }, { default: Cookies }] = await Promise.all([
      import("./helpers/storageManager.js"),
      import("js-cookie"),
    ]);
    let redirected:any = getStorageItem("isRedirected")
    let comparePath = redirected?.path?.split("?")
    comparePath = comparePath?.length ? comparePath[0] : redirected?.path
    const currentPath = window.location.pathname
    if (currentPath !== "/authentication" && (currentPath === comparePath || currentPath === "/")) {
      const onDetailMidFlow = currentPath === comparePath && redirected?.isDetailPage && !redirected?.redirect
      if (!onDetailMidFlow) {
        setStorageItem("isRedirected", {
          ...redirected,
          redirect: false,
          isDetailPage: currentPath === "/" ? !!redirected?.isDetailPage : false,
          isUpgradePlan: false,
          isCheckout: false,
        })
      }
      if(!redirected?.contentSubscriptionFlow?.enabled || currentPath === redirected?.path) {
        sessionStorage.removeItem("contentPlanIds")
        sessionStorage.removeItem("toShowTve")
      }
      sessionStorage.removeItem('isUpgradeAvailable')
    }
    if(currentPath != '/redeem-authentication') {
      Cookies.set('RedirectToRedeem', false)
    }
  }
  const redirectToPath = (data) => {
    if (isNavigationBlockedByPreview()) return;
    const navigateToExternalUrl = helpers.isNavigateToExternalUrl();
    if (navigateToExternalUrl) {
      window.location.href = navigateToExternalUrl;
      return
    } else {
      navigate(data)
    }
  }
  useEffect(() => {
    if (sessionStorage.getItem('reloadAfterNavigation') === 'true') {
      sessionStorage.removeItem('reloadAfterNavigation');
      window.location.reload();
      return;
    }
    pubsub.subscribe('redirect',redirectToPath)

    // Keycloak sign-in initiation on /authentication path because to prevent /authentication from being rendered before the Keycloak redirect occurs. This avoids a flash of the login page before the redirect.
    if (keycloakEnabled && location.pathname === "/authentication") {
      initKeycloakSignIn().catch((err) => {
        console.error("Keycloak sign-in initiation error:", err);
      });
      return;
    }

    const searchTerm = location.state?.searchTerm || null;
    // Don't publish 'apploading: false' here — pageClientQueryData publishes it
    // on its own when the fetch resolves (success, error, or timeout). Hiding
    // the loader synchronously before the fetch completes is what makes
    // navigations look blank/stale between URL change and new page render.
    if (isPreviewMode) {
      pubsub.publish('apploading', false);
    } else {
      helpers.pageClientQueryData(pageData,setPageData,{ status: false },searchTerm)
    }
    loadSiteStyles()

    let bodyTag = document.getElementsByTagName("body")
    if (bodyTag.length > 0){
       bodyTag[0].style.overflowY = "unset"
       bodyTag[0].style.position = "unset"}

    afterPaint(async () => {
      const { initializeWebWorker } = await import("../registerSW.js");
      initializeWebWorker()
      sessionStorage.setItem("transactionStarted", JSON.stringify(false)) 

      let isCheckoutInitiate = parseJSON(sessionStorage.getItem('checkout-initiate') || '')
      if (isCheckoutInitiate) {
        sessionStorage.removeItem('checkout-initiate')
        let currentStoredPlan = parseJSON(sessionStorage.getItem('currentPlan') || '')
        const checkoutInitPageDetails = parseJSON(sessionStorage.getItem('checkoutInitPageData') || '')
        let eventData = {
            "pageUrl": checkoutInitPageDetails?.pageUrl,
            "monetizationType": currentStoredPlan?.type,
            "message": 'page change'
        }
        pubsub.publish('user-analytics',{ename:ANALYTICSEVENTS.NEW_ONBOARDING_CHECKOUT_CANCELED, data: eventData});
        helpers.handleStatusGaCalls({eventName : 'checkout_cancellation',subSection : null, type : 'cancellation', reason : 'page change', status : 'cancellation', action : 'Cancel', paymentStatus : null,eventCategory : 'Checkout',section : 'checkout',additionalLabel : null})
      }

      if(pageData?.path != '/tos' && pageData?.path != '/privacy-policy' && pageData?.path != '/offers' && pageData?.path != '/agreement' && pageData?.path != '/checkout') helpers.checkVppaCompliance()
      checkForIsRedirected()
      syncSSEStore()
      if(location?.pathname === '/'){
        sessionStorage.removeItem("contentPlanIds");
        sessionStorage.removeItem("toShowTve")
        sessionStorage.removeItem("splashLoginEmail")
        sessionStorage.removeItem("splashSignupEmail")
      }
      const isTveEnabled = helpers.isTveOnlyLoginEnabled()
      startTransition(() => {
        setIsTveEnabled(isTveEnabled)
      })
      if(isTveEnabled && (location.pathname == '/authentication' || location.pathname == '/checkout')){
        navigate('/')
      }  
      if (typeof (window) !== undefined && window?.app_data?.appcmsPlatform?.analytics?.convivaConfig?.enabled){
        pubsub.publish('conviva-page-change',null);
      }
    });
  }, [location]);

  // A previewed page has no page behind its URL, so a link cannot lead anywhere: the
  // router would change the URL and re-render the same posted modules, and a full
  // navigation would leave the preview for the published site. Refusing the click keeps
  // the draft on screen, which is the only thing here worth looking at.
  useEffect(() => {
    if (!isPreviewMode) return;
    return blockPreviewNavigation();
  }, [isPreviewMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Published module updates are irrelevant to a preview of unpublished content.
    if (isPreviewMode) return;
    let timeout: NodeJS.Timeout | null = null;
    afterPaint(() => {
      try {
        pubsub.subscribe('page-modules-updated', (data?: { updatedModules: string[]; pageId: string; site: string }) => {
          const currentPageMetaData = pageMetaDataRef.current;
          if (!data || !data.updatedModules || data.updatedModules.length === 0 || !data.pageId || !data.site)
            return console.warn('Invalid data received for page-modules-updated event');
          if (data.pageId !== currentPageMetaData?.page?.id)
            return console.info('User is not on the same page where modules are updated');
          const scheduledPath = window?.location?.pathname || currentPageMetaData?.path;
          const randomDelay = Math.floor(Math.random() * (50000 - 5000 + 1)) + 5000;
          console.info(`Received page-modules-updated event. Fetching updated module data in ${randomDelay} ms...`);
          if (timeout) clearTimeout(timeout);
          timeout = setTimeout(() => {
            const latestPageMetaData = pageMetaDataRef.current;
            const hasNavigatedToDifferentPage = latestPageMetaData?.page?.id !== data.pageId;
            const hasPathChanged = window?.location?.pathname !== scheduledPath;
            if (hasNavigatedToDifferentPage || hasPathChanged)
              return console.info('Skipping delayed module refresh because user navigated to a different page');
            console.info('Fetching updated module data after page-modules-updated event...');
            helpers.pageClientQueryData(latestPageMetaData, setPageData, { status: false }, null, data.updatedModules, true);
          }, randomDelay);
        });
      } catch (err) {
        console.error(`Error subscribing to page-modules-updated event: ${err}`);
      }
    });
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  const handleUserCreated = async (data) => {
    if (data?.status) {
      initializeConnection({
        site: siteConfig?.site?.siteInternalName,
        sseBaseUrl: sseBaseUrl
      });
      const { unsubscribeUserCreated } = await import("./OnBoarding-V2/Events/utils.events");
      unsubscribeUserCreated((data) => handleUserCreated(data))
    }
  }
  const clevertapInit = async () => {
    const [{ getStorageItem }, { requestUserPermission, anonymousUserPushNotification }] = await Promise.all([
      import("./helpers/storageManager.js"),
      import("../registerSW.js"),
    ]);
    const AuthenticationStore: any = getStorageItem('AuthenticationStore')
    let userInfo = AuthenticationStore?.user
    if (userInfo?.id) {
      var subscriptionData = userInfo.subscription || {}
      var userProfile = {
        'Name': userInfo.name,
        'Email': userInfo.email,
        'Identity': userInfo.userId,
        'Phone': userInfo && userInfo.phoneNumber ? ((typeof userInfo.phoneNumber === 'string' && (userInfo.phoneNumber.charAt(0) !== '+')) ? JSON.parse(userInfo.phoneNumber) : userInfo.phoneNumber) : "",
        'User Status': isUserSubscribed() ? 'Subscribed' : 'Registered and Never Subscribed',
        'registration_method': userInfo.provider,
        'registration_platform': userInfo.registerdVia && helpers.capitalize(userInfo.registerdVia.split('_')[0]),
        'registration_date': helpers.getUnixTimestamp((userInfo.registeredOn && userInfo.registeredOn.split("T")[0]) || null),
        'registration_country': userInfo.country,
        'MSG-whatsapp': userInfo && userInfo.whatsappConsent ? userInfo.whatsappConsent : false,
        'MSG-email': userInfo && userInfo?.emailConsent ? userInfo.emailConsent : false,
      }
      if (isUserSubscribed() && subscriptionData && subscriptionData.subscriptionInfo) {
        Object.assign(userProfile, {
          'paymentHandler': subscriptionData?.subscriptionInfo?.paymentHandler || null,
          'subscriptionStartDate': helpers.getUnixTimestamp((subscriptionData?.subscriptionInfo?.subscriptionStartDate && subscriptionData?.subscriptionInfo?.subscriptionStartDate.split("T")[0]) || null),
          'subscriptionEndDate': helpers.getUnixTimestamp((subscriptionData?.subscriptionInfo?.subscriptionEndDate && subscriptionData?.subscriptionInfo?.subscriptionEndDate.split("T")[0]) || null),
          'discountAmount': subscriptionData?.subscriptionInfo?.planAmount || null,
          'paymentPlanID': subscriptionData?.subscriptionInfo?.planId || null,
          'freeTrial': subscriptionData?.subscriptionInfo?.freeTrial ? 'Yes' : 'No',
          'country': subscriptionData?.subscriptionInfo?.countryCode || null,
          'currency': subscriptionData?.subscriptionInfo?.currencyCode || null,
          'amount': subscriptionData?.subscriptionInfo?.planAmount || null,
        })
      }
      import("./clevertap")
      .then(({ initCleverTap, addUserProfile }) => {
        return Promise.resolve(initCleverTap())
          .then(() => Promise.resolve(addUserProfile('Site', userProfile, true)));
      })
      .catch(error => {
        console.error("Error executing clevertap functions:", error);
      });
      requestUserPermission();
    } else {
      import("./clevertap")
      .then(({ initCleverTap }) => {
        return Promise.resolve(initCleverTap())
        .then(()=> anonymousUserPushNotification?.())
      })
      .catch(error => {
        console.error("Error executing clevertap functions:", error);
      });
    }
  }


  const initiateInterval = async () => {
    const { BroadcastChannel } = await import("broadcast-channel");
    let storageName = `${siteConfig.site?.siteInternalName}-lastTimestamp`
    IntervalInstance = setInterval(()  => {
      let lastTimestamp = localStorage.getItem(storageName)
      let broadcastHeartbeat = sessionStorage.getItem('broadcast-heartbeat')
      let currentTime = new Date().getTime();
      const channelName = `${siteConfig?.site?.siteInternalName}-channel`
      const bc = new BroadcastChannel(channelName);
      if ((!broadcastHeartbeat || (currentTime - parseInt(broadcastHeartbeat)) > 20000) && lastTimestamp && (currentTime - parseInt(lastTimestamp)) < 20000) {
        helpers.listenToChannel(bc)
      }
      if(!lastTimestamp || (currentTime - parseInt(lastTimestamp)) > 20000) {
        initializeConnection({
          site: siteConfig?.site?.siteInternalName,
          sseBaseUrl: sseBaseUrl
        });
      }
    },20000)
  }

  useEffect(() => {
    afterPaint(() => clevertapInit());
  }, []);

  useEffect(()=> {
    afterPaint(async () => {
      const [{ default: Cookies }, { BroadcastChannel }, { registerCustomSW }, { pushAdobeAnalyticsData }, { getStorageItem }, { subscribeUserCreated }] = await Promise.all([
        import("js-cookie"),
        import("broadcast-channel"),
        import("../registerSW.js"),
        import("./AdobeAnalytics/AdobeExtention.js"),
        import("./helpers/storageManager.js"),
        import("./OnBoarding-V2/Events/utils.events"),
      ]);

      let isTveAuthenticated = helpers.handleQueryUrls()
      registerCustomSW()
      if (typeof window !== 'undefined' && !helpers.isContentPage()) {
        helpers.setPageTree()
        if(isTveAuthenticated) return
        pushAdobeAnalyticsData({eventName : "Page Load", config : {type : "page load"}})
      }
      let storageName = `${siteConfig.site?.siteInternalName}-lastTimestamp`
      let lastTimestamp = localStorage.getItem(storageName)
      let currentTime = new Date().getTime();
      const channelName = `${siteConfig?.site?.siteInternalName}-channel`
      const bc = new BroadcastChannel(channelName);
      const adobeVersion = window?.app_data?.appcmsPlatform?.tveSettings?.version || "v1"
      if(!lastTimestamp || (currentTime - parseInt(lastTimestamp)) > 20000) {
        initializeConnection({
          site: siteConfig?.site?.siteInternalName,
          sseBaseUrl: sseBaseUrl
        });
        initiateInterval();
      }else{
        bc.postMessage("ping");
        helpers.listenToChannel(bc)
        initiateInterval();
        logger("joined a existing channel", "info")
      }
      (analyticListenerReady.current || Promise.resolve()).then(() => {
        helpers.handlePageViewEvent(pageData,{ status : false })
      })
      helpers.listenDisposeCookies();
      helpers.listenDeviceRemoved();
      if( window && typeof(window) !== undefined){
        const store = getStorageItem('AuthenticationStore')
        let token = Cookies.get('token') && JSON.parse(Cookies.get('token'))
        if (store?.user?.userId) {
          await helpers.handleUserTransactions()
          if(!token?.refreshToken) helpers.fireLogout();
          if(helpers.handleSeviceTypes().TVE && adobeVersion == 'v1'){
            import('./helpers/loadTve').then(async(module) => {
              let loadtve = module.default
              await loadtve()
            })
          }
        }
        helpers.handleUserLogout(store?.user?.userId)
      }
      helpers.fetchAndSetupPlanGroups()
      subscribeUserCreated((data) => handleUserCreated(data))
    });
    return(() => {
      clearInterval(IntervalInstance)
    })
  },[])

  useEffect(() => {
    afterPaint(async () => {
    const queryString = document.location.search.slice(1)
    if (queryString.includes('utm_')) {
      import('./utm').then((utmFunc) => {
        let {utm} = utmFunc
        utm()
      })
    }

      const [{ isBrazeEnabled }, { getStorageItem }] = await Promise.all([
        import("../registerSW.js"),
        import("./helpers/storageManager.js"),
      ]);
      const brazeObj = isBrazeEnabled()
      if(brazeObj.enabled){
        const forSignedUserOnly = !!brazeObj?.forSignedUserOnly
        let store: any = getStorageItem('AuthenticationStore');
        let userId = store?.user?.userId
        let brazeInitialised = false

        const initialiseBraze = (identity?: { userId?: string; email?: string }) => {
          if (brazeInitialised) return
          brazeInitialised = true
          import(
            /* webpackExports: ["initialize", "openSession","requestPushPermission","isPushSupported","subscribeToInAppMessage","isPushPermissionGranted","isPushBlocked","InAppMessage","showInAppMessage","automaticallyShowInAppMessages"] */
            "@braze/web-sdk"
          ).then(async ({ initialize, openSession,requestPushPermission,isPushSupported,automaticallyShowInAppMessages,isPushPermissionGranted,isPushBlocked,changeUser,getUser}) => {
            initialize(brazeObj?.apiKey, {
              baseUrl: brazeObj?.baseUrl,
              enableLogging: true,
              manageServiceWorkerExternally:true
            });
            requestPushPermission();
            automaticallyShowInAppMessages();

            const resolvedUserId = identity?.userId
            const resolvedEmail = identity?.email
            if (resolvedUserId && typeof changeUser === 'function') {
              changeUser(resolvedUserId)
              if (resolvedEmail && typeof getUser === 'function') getUser()?.setEmail(resolvedEmail)
            }

            if (typeof openSession === 'function') openSession()

            if (typeof window !== "undefined") {
              window.addEventListener("beforeunload",(evt) => {
                console.log("App Session End")
                import('./helpers/trackAnalytics')
                .then(({trackBrazeEvents}) => {
                  trackBrazeEvents('App Session End',true)
                })
              })
            }
          })
          .catch((err) => {
            brazeInitialised = false
            console.log(err,"error in initialising braze sdk")
          })
        }

        if (forSignedUserOnly) {
          // Do not load/init Braze for anonymous visitors when forSignedUserOnly is set.
          if (userId) {
            initialiseBraze({ userId, email: store?.user?.email })
          } else {
            pubsub.subscribe('isAuthenticated',(data) => {
              const storeAfterAuth: any = getStorageItem('AuthenticationStore');
              const brazeUserId =
                data?.id ||
                data?.userId ||
                storeAfterAuth?.user?.userId ||
                storeAfterAuth?.user?.id;
              if (!brazeUserId) return
              initialiseBraze({
                userId: brazeUserId,
                email: data?.email || storeAfterAuth?.user?.email,
              })
            })
          }
        } else {
          initialiseBraze(userId ? { userId, email: store?.user?.email } : undefined)
          if (!userId) {
            pubsub.subscribe('isAuthenticated',(data) => {
              const storeAfterAuth: any = getStorageItem('AuthenticationStore');
              const brazeUserId =
                data?.id ||
                data?.userId ||
                storeAfterAuth?.user?.userId ||
                storeAfterAuth?.user?.id;
              if (!brazeUserId) return
              import(
                /* webpackExports: ["changeUser","getUser"] */
                "@braze/web-sdk"
              ).then(({ changeUser, getUser }) => {
                if (typeof changeUser === 'function') changeUser(brazeUserId)
                const email = data?.email || storeAfterAuth?.user?.email
                if (email && typeof getUser === 'function') getUser()?.setEmail(email)
              })
            })
          }
        }
      }
    });
  }, []);


  useEffect(() => {
    afterPaint(() => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.controller?.postMessage({ type: 'app_data', payload: window?.app_data?.appcmsMain?.analytics?.firebaseConfig });
      }
      if(typeof window !== "undefined"){
        import('./fetchBulkEntitlement').then(({getBulkEntitlementData})=>{
          getBulkEntitlementData()
        })
      }
    });
  }, []);

  // useEffect(() => {
  //   window.addEventListener('load', () => {
  //     setTimeout(() => {
  //       const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  
  //       const SLOW_ASSET_THRESHOLD = 900; // ms
  //       const slowAssets = entries
  //         .filter(entry =>
  //           entry.initiatorType !== 'xmlhttprequest' && entry.duration > SLOW_ASSET_THRESHOLD
  //         )
  //         .map(entry => ({
  //           name: entry.name,
  //           initiatorType: entry.initiatorType,
  //           transferSize: entry.transferSize,
  //           duration: entry.duration,
  //         }));
  //         const vitals = {
  //           LCP: performance.getEntriesByName('largest-contentful-paint')[0]?.startTime,
  //           FID: (performance.getEntriesByName('first-input')[0] as PerformanceEventTiming)?.processingStart,
  //           CLS: performance.getEntriesByType('layout-shift')
  //           .reduce((a, b) => a + (b as LayoutShift).value, 0),
  //           slowAssets : slowAssets
  //         };
  
  //       if (slowAssets.length > 0) {
  //         navigator.sendBeacon('/performance', JSON.stringify(vitals));
  //       }
  //     }, 0);
  //   });
  // }, []);

  // const lcp = useRef<number | undefined>();
  // const fid = useRef<number | undefined>();
  // const cls = useRef(0);

  // useEffect(() => {
  //   let lcpObserver: PerformanceObserver;
  //   let fidObserver: PerformanceObserver;
  //   let clsObserver: PerformanceObserver;

  //   if ('PerformanceObserver' in window) {
  //     // LCP
  //     lcpObserver = new PerformanceObserver((list) => {
  //       const entries = list.getEntries();
  //       const lastEntry = entries[entries.length - 1];
  //       lcp.current = lastEntry.startTime;
  //     });
  //     lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

  //     // FID
  //     fidObserver = new PerformanceObserver((list) => {
  //       const entry = list.getEntries()[0] as PerformanceEventTiming;
  //       if (entry) {
  //         fid.current = entry.processingStart - entry.startTime;
  //       }
  //     });
  //     fidObserver.observe({ type: 'first-input', buffered: true });

  //     // CLS
  //     clsObserver = new PerformanceObserver((list) => {
  //       for (const entry of list.getEntries() as LayoutShift[]) {
  //         if (!entry.hadRecentInput) {
  //           cls.current += entry.value;
  //         }
  //       }
  //     });
  //     clsObserver.observe({ type: 'layout-shift', buffered: true });
  //   }

  //   window.addEventListener('load', () => {
  //     setTimeout(() => {
  //       const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  //       const SLOW_ASSET_THRESHOLD = 900;
  //       const slowAssets = entries
  //         .filter(entry =>
  //           entry.initiatorType !== 'xmlhttprequest' && entry.duration > SLOW_ASSET_THRESHOLD
  //         )
  //         .map(entry => ({
  //           name: entry.name,
  //           initiatorType: entry.initiatorType,
  //           transferSize: entry.transferSize,
  //           duration: entry.duration,
  //         }));

  //       const vitals = {
  //         LCP: lcp.current,
  //         FID: fid.current,
  //         CLS: cls.current,
  //         slowAssets,
  //       };
  //       if (slowAssets.length > 0) {
  //         try {
  //           const payload = JSON.stringify(vitals);
  //           console.log('[Vitals Payload]', payload);
  //           const sent = navigator.sendBeacon('/performance', payload);
  //           console.log('[Beacon Sent]', sent); // Should log `true`
  //         } catch (err) {
  //           console.error('[Beacon Error]', err);
  //         }
  //       }
  //     }, 0);
  //   });

  //   return () => {
  //     lcpObserver?.disconnect();
  //     fidObserver?.disconnect();
  //     clsObserver?.disconnect();
  //   };
  // }, []);
  

  useEffect(() => {
    if (typeof window === "undefined") return
    import("./components/Snackbar").then((component) => {
      startTransition(() => setSnackbarComponent(() => component.default));
      helpers.verifyMagicLink();
    })
  }, []);

  useEffect(() => {
    if (typeof window === undefined) return
    pubsub.subscribe("magic-link", (data: {success:boolean, message:string}) => {
      snackbarRef?.current?.openSnackbar({message: (data?.message && JSON.parse(data?.message)), type: data?.success ? "success" : "error"}, 5000)
    });
  }, [])
  
  useEffect(() => {
    function loadSplash() {
      const splashConfig = window?.app_data?.appcmsMain?.splashConfig;
      if (!splashConfig) return
      const isObject = (typeof splashConfig === 'object' && !Array.isArray(splashConfig) && splashConfig !== null) ? true : false
      if(!isObject) return
      if(Object.keys(splashConfig)?.length <= 0) return 
      const activeSplash = Object.keys(splashConfig)?.find((splash) => (splashConfig[splash]?.isActive ? splash : null));
      if (!activeSplash) return false;
      startTransition(() => setActiveSplash(activeSplash as string));
    }
    loadSplash();
    return () => {};
  }, []);

  useEffect(() => {
    async function isUserAvailable(planId) {
      const { isSubscribed, user } = await helpers.fetchUserDetails();
      if (planId && user && isSubscribed) {
        let userPlanId = user?.subscription?.subscriptionInfo?.planId
        if (planId !== userPlanId ) {
          const planDetails = await helpers.getPlanByPlanId(planId);
          if (planDetails && planDetails?.length) {
            const plan = helpers.structuredPlan(planDetails[0]);
            sessionStorage.setItem('currentPlan', JSON.stringify(plan));
            setIsUpgrade(true);
          }
        }
      }
    }

    if(window && typeof(window) !== undefined) {
      const queryPlanId = searchParams.get('planId') || '';
      const popup = searchParams.get('popup') || '';
      if (location.pathname === "/" && queryPlanId && popup == 'upgradeSubscription') {
        isUserAvailable(queryPlanId);
        pubsub.subscribe("hide-upgrade-popup", (data) => {
          if(data) setIsUpgrade(false)
        });
      }
    }
  },[location]);

  let pageSchema = {}
    const publisher = siteConfig?.appcmsMain?.companyName
    const publisherLogo = siteConfig?.appcmsPlatform?.images?.desktopLogo ? siteConfig?.appcmsPlatform?.images?.desktopLogo+'?impolicy=resize&w=960&h=540': ""
    pageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": pageMetaData?.page?.metadataMap?.title || pageMetaData?.page?.title,
      "url": `https://${browserDetails?.host}${pageMetaData?.path ?? ""}`,
      "description": pageMetaData?.page?.metadataMap?.description || pageMetaData?.page?.title,
      "publisher": {
        "@type": "Organization",
        "name": publisher,
        "logo": {
          "@type": "ImageObject",
          "url": publisherLogo
        }
      }
    }

  const {
    VerticalMasthead,
    Masthead,
    isVerticalMasthead,
    originalPageData,
    pageDataClone,
    mastheadData,
    horizontalMasthead
  }: any = useMemo(() => {
    if (pageMetaData?.error) {
      return {};
    }
    const originalPageData = JSON.parse(JSON.stringify(pageMetaData?.page));
    const pageDataClone = JSON.parse(JSON.stringify(originalPageData));

    const mastheadData = originalPageData?.modules?.filter(
      module => module.moduleType === "MastheadModule"
    );
    const remainingData = originalPageData?.modules?.filter(
      module => module.moduleType !== "MastheadModule"
    );
    originalPageData.modules = remainingData;

    let isVerticalMasthead = false;
    let horizontalMasthead = false;
    if (mastheadData?.length > 0) {
      const placement = mastheadData[0]?.layout?.settings?.navigationPlacement;
      if (placement === "Left") {
        isVerticalMasthead = true;
      } else {
        horizontalMasthead = true;
      }
    }

    return {
      VerticalMasthead: isVerticalMasthead ? VerticalMastheadComponent : null,
      isVerticalMasthead,
      Masthead: horizontalMasthead ? MastheadComponent : null,
      originalPageData,
      pageDataClone,
      horizontalMasthead,
      mastheadData
    };
  }, [pageMetaData?.page]);

  const tveProviderOperator =
    (typeof window !== "undefined"
      ? window?.app_data?.appcmsPlatform?.tveSettings?.tveProvider
      : siteConfig?.appcmsPlatform?.tveSettings?.tveProvider) || ""

  if(pageMetaData?.error) {
    return (
      <Html data-testid="html" xApiKey={xApiKey} cachedAPI={cachedAPI} sseBaseUrl={sseBaseUrl} siteConfig={siteConfig} pageData={pageMetaData} browserDetails={browserDetails} title={siteConfig?.appcmsMain?.site} apiBaseUrl={apiBaseUrl}  analyticsBaseUrl={analyticsBaseUrl}  wssBaseUrl={wssBaseUrl} env={env} ip={ip} embedCodeToken={embedCodeToken} toolsUrl={toolsUrl} telemetryIngestEndpoint={telemetryIngestEndpoint} templateUrl={templateUrl} requestId={requestId} cssHref={cssHref} scriptSrcs={scriptSrcs} isPreview={isPreviewMode}>
        <Suspense>
          <ErrorBoundary fallback={<div>Error loading component</div>}>
            <div className="main"><NotFound404 /></div>
          </ErrorBoundary>
        </Suspense>
      </Html>
    )
  } else {
    return (
      <Html data-testid="html" xApiKey={xApiKey} cachedAPI={cachedAPI} sseBaseUrl={sseBaseUrl} siteConfig={siteConfig} pageData={pageMetaData} browserDetails={browserDetails} title={siteConfig?.appcmsMain?.site} apiBaseUrl={apiBaseUrl}  analyticsBaseUrl={analyticsBaseUrl}  wssBaseUrl={wssBaseUrl} env={env} ip={ip} embedCodeToken={embedCodeToken} toolsUrl={toolsUrl} telemetryIngestEndpoint={telemetryIngestEndpoint} templateUrl={templateUrl} requestId={requestId} cssHref={cssHref} scriptSrcs={scriptSrcs} isPreview={isPreviewMode}>
        <Suspense>
        <noscript
          dangerouslySetInnerHTML={{
            __html: `
              <style>
                .skip-nav-noscript {
                  position: static;
                  display: block;
                  background: #3c763d;
                  color: white;
                  padding: 10px;
                  text-decoration: underline;
                }
              </style>
              <a href="#main" class="skip-nav-noscript">Skip to main content</a>
            `,
          }}
        />
          <NavFocusSkipper />
        {/* React 19 keeps inline <script> tags in place and dedupes them by
            content, so we render JSON-LD directly instead of going through
            react-helmet's subscription/SSR shim. */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
        />
        {activeSplash ? <SplashLoader name={activeSplash} /> : false}
        <PersonalizationBanner/>
        <CmsMenu />
        {shouldShowLocationModal && <LocationServicesModal />}
        {showNbaModal && <ContinueSubscriptionModal />}
        {isUpgrade ? <UpgradeWrapper metadataMap="" isViewplans={false} /> : <></>}
        {isTveEnabled && <V2TveLogin previouslyFocusedElement={previouslyFocusedElement} type="TveModule" />}
        {tveProviderOperator === "toolbox" && <AuthZFailPopupContainer />}
        {isVerticalMasthead ? <VerticalMasthead tabIndex={-1} pageData={pageMetaData?.page?.modules} siteConfig={siteConfig} moduleData={mastheadData?.[0]} /> : <></>}
        {horizontalMasthead ? <Masthead pageData={pageDataClone} siteConfig={siteConfig} moduleData={mastheadData?.[0]} /> : <></>}
        <main id="main">
            <div className="modules-wrapper" style={isVerticalMasthead ? { marginLeft: '250px' } : {}}>
              {SnackbarComponent && <SnackbarComponent ref={snackbarRef} />}
              <LazyLoadModules browserDetails={browserDetails} offset={4} pageData={originalPageData} pathName={pageMetaData?.path} siteConfig={siteConfig} />
            </div>
          </main>
         {VLCookieConsent && !disableVlCookieConsent && <VLCookieConsent genericMessage={configMessages} />}
        </Suspense>
      </Html>
  );
  }
};
